import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, MessageSquare, Paperclip, Clock } from "lucide-react";
import { DashboardShell } from "../../components/shared/DashboardShell";
import { adminNav } from "../../components/shared/navConfig";
import api from "../../lib/api";

const TABS = [
  { key: "active",      label: "Active" },
  { key: "pending",     label: "Pending Response" },
  { key: "in_progress", label: "In Progress" },
  { key: "submitted",   label: "Submitted" },
  { key: "revision",    label: "Revision" },
  { key: "completed",   label: "Completed" },
  { key: "archived",    label: "Archived" },
];

const STATUS_TONE = {
  sent:        "#3B82F6", accepted:  "#10B981", rejected:  "#F43F5E",
  in_progress: "#06B6D4", submitted: "#EAB308", revision:  "#F97316",
  approved:    "#10B981", completed: "#10B981", archived:  "#64748B",
};

const fmtRel = (iso) => {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function AdminDirectWork() {
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/direct-work", { params: { tab, q: q || undefined } });
      setRows(data);
    } catch (_) { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const headerRight = (
    <Link to="/admin/direct-work/new" data-testid="adw-start-btn"
          className="btn-primary px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1.5">
      <Plus size={12}/> Start Direct Work
    </Link>
  );

  return (
    <DashboardShell nav={adminNav} title="Direct Work" subtitle="Assign work directly to a verified student." headerRight={headerRight}>
      {/* Search + tabs */}
      <div className="flex flex-col md:flex-row gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-2 rounded-md border flex-1"
             style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}>
          <Search size={14} style={{ color: "var(--text-dim)" }}/>
          <input
            data-testid="adw-search-input"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => e.key === "Enter" && load()}
            placeholder="Search by title, student, ID…"
            className="bg-transparent outline-none text-sm w-full"
            style={{ color: "var(--text)" }}
          />
        </div>
      </div>
      <div className="flex gap-1.5 mb-5 flex-wrap" data-testid="adw-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            data-testid={`adw-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className="px-3 py-1.5 rounded-full text-xs border transition-colors"
            style={{
              background: tab === t.key ? "rgba(59,130,246,0.10)" : "var(--surface)",
              borderColor: tab === t.key ? "rgba(59,130,246,0.35)" : "var(--border-strong)",
              color:  tab === t.key ? "var(--brand)" : "var(--text-muted)",
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* Cards */}
      {loading ? (
        <div className="text-sm py-10 text-center" style={{ color: "var(--text-dim)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <MessageSquare size={22} className="mx-auto mb-3" style={{ color: "var(--text-dim)" }}/>
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>
            No direct-work conversations in this tab.
          </div>
          <Link to="/admin/direct-work/new" className="btn-primary mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs">
            <Plus size={12}/> Start Direct Work
          </Link>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map(r => (
            <Link
              key={r.direct_work_id}
              to={`/admin/direct-work/${r.direct_work_id}`}
              data-testid={`adw-card-${r.direct_work_id}`}
              className="card-surface card-hover p-4 block"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-[10px] font-mono" style={{ color: "var(--text-dim)" }}>{r.direct_work_id}</div>
                  <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>{r.title}</div>
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full border"
                      style={{ borderColor: STATUS_TONE[r.status] + "55", color: STATUS_TONE[r.status], background: STATUS_TONE[r.status] + "10" }}>
                  {r.status}
                </span>
              </div>
              <div className="text-xs mb-3 truncate" style={{ color: "var(--text-muted)" }}>
                {r.student_name} · {r.city || r.location || "Remote"}
              </div>
              <div className="flex items-center justify-between text-[11px]" style={{ color: "var(--text-dim)" }}>
                <span className="inline-flex items-center gap-1"><Clock size={11}/> {r.deadline || "no deadline"}</span>
                <span>Last activity {fmtRel(r.last_message_at)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11px]">
                <span style={{ color: "var(--text-dim)" }}>
                  {r.attachment_file_ids?.length ? <Paperclip size={11} className="inline mr-1"/> : null}
                  {r.attachment_file_ids?.length || 0} attachments
                </span>
                {r.unread_admin > 0 && (
                  <span className="px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/40 text-rose-500">
                    {r.unread_admin} new
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
